/**
 * Region Grid Component
 * 
 * Displays the main grid of regions with bar numbers and grid lines.
 */

import React from 'react';
import { Region } from './Region';
import { BARS_PER_LINE, MIN_BARS_FOR_FULL_DISPLAY, LINES_PER_PAGE, BARS_PER_PAGE } from '../constants/grid';
import { getColumnStart, getColumnEnd } from '../utils/regionLayout';
import './RegionGrid.css';

/**
 * Bar number display
 */
function BarNumber({ number, isLight }) {
  if (number === null) {
    return <span className="bar-number-empty">&nbsp;</span>;
  }
  
  const className = isLight ? 'bar-number-light' : 'bar-number-dark';
  return <span className={className}>&nbsp;{number}</span>;
}

/**
 * Bar numbers overlay
 */
function BarNumbers({ currentPage }) {
  const numbers = [];
  const pageStartBar = (currentPage - 1) * BARS_PER_PAGE;

  for (let i = 0; i < LINES_PER_PAGE; i++) {
    // Left padding
    numbers.push(<BarNumber key={`pad-left-${i}`} number={null} />);

    for (let j = 0; j < BARS_PER_LINE; j++) {
      const bar = pageStartBar + i * BARS_PER_LINE + j;
      const barNumberForDisplay = bar + 1;
      
      if (bar % 4 === 0) {
        numbers.push(<BarNumber key={`bar-${bar}`} number={barNumberForDisplay} />);
      } else {
        numbers.push(<BarNumber key={`bar-${bar}`} number={null} />);
      }
    }

    // Right padding
    numbers.push(<BarNumber key={`pad-right-${i}`} number={null} />);
  }
  return <div className="numbers-grid">{numbers}</div>;
}

/**
 * Grid lines background
 */
function GridLines() {
  const lines = [];
  for (let col = 2; col <= BARS_PER_LINE + 2; col++) {
    lines.push(
      <div
        key={col}
        className="grid-line"
        style={{ gridColumn: col }}
        aria-hidden="true"
      />
    );
  }
  return <div className="grid-lines" aria-hidden="true">{lines}</div>;
}

/**
 * Render a single region segment with positioning
 */
function RegionSegment({ segment }) {
  const { lineStart, lineEnd, arrow, lineNumber } = segment;
  const isShort = (lineEnd - lineStart) < MIN_BARS_FOR_FULL_DISPLAY;
  
  // Calculate grid positioning
  const isLineContinuation = arrow === 'l' || arrow === 'rl';
  const columnStart = isLineContinuation ? 1 : getColumnStart(lineStart);
  const columnEnd = getColumnEnd(lineStart, lineEnd, isLineContinuation);
  
  // Calculate row position based on line number
  const rowStart = (lineNumber % 6) + 1; // 6 lines per page, 1-indexed
  
  // Show bar number for region start (if not continuation)
  const showBarNumber = !isLineContinuation && (lineStart % 4 !== 0);
  const barNumber = showBarNumber ? lineStart + 1 : null;
  
  return (
    <div
      className="region-segment"
      style={{
        gridColumnStart: columnStart,
        gridColumnEnd: columnEnd,
        gridRowStart: rowStart,
        gridRowEnd: rowStart + 1,
      }}
    >
      {showBarNumber && <BarNumber number={barNumber} isLight={true} />}
      <Region region={segment} arrow={arrow} isShort={isShort} />
    </div>
  );
}

/**
 * Main region grid component
 * 
 * @param {object} props - Component props
 * @param {Array} props.regions - Array of region segments for current page
 * @param {number} props.currentPage - Current page number
 */
export function RegionGrid({ regions, currentPage }) {
  return (
    <div className="grid-container">
      <GridLines />
      <BarNumbers currentPage={currentPage} />
      <div className="regions-grid">
        {regions.map((segment, index) => (
          <RegionSegment key={`${segment.id}-${index}`} segment={segment} />
        ))}
      </div>
    </div>
  );
}


